
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useAuth } from "@/components/AuthProvider";
import { LoadingButton } from "@/components/ui/loading-button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/components/LanguageContext";
import { useTranslations } from "@/hooks/useTranslations";

type ScheduleSettingsForm = {
  work_start_time: string;
  work_end_time: string;
  working_days: string[];
  slot_duration: number;
};

export function ScheduleSettings({ onSave }: { onSave?: () => void }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const { language } = useLanguage();
  const { t } = useTranslations("schedule");

  const weekDays = [
    { value: "0", label: "weekDays.sunday" },
    { value: "1", label: "weekDays.monday" },
    { value: "2", label: "weekDays.tuesday" },
    { value: "3", label: "weekDays.wednesday" },
    { value: "4", label: "weekDays.thursday" },
    { value: "5", label: "weekDays.friday" },
    { value: "6", label: "weekDays.saturday" },
  ];

  const form = useForm<ScheduleSettingsForm>({
    defaultValues: {
      work_start_time: "09:00",
      work_end_time: "20:00",
      working_days: ["1", "2", "3", "4", "5"],
      slot_duration: 60,
    },
  });

  useEffect(() => {
    const fetchSettings = async () => {
      if (!user) return;

      try {
        const { data, error } = await supabase
          .from("schedule_settings")
          .select()
          .eq("trainer_id", user.id)
          .single();

        if (error) {
          if (error.code !== 'PGRST116') { // Not found error
            console.error("Error fetching settings:", error);
          }
          return;
        }

        if (data) {
          form.reset({
            work_start_time: data.work_start_time.slice(0, 5),
            work_end_time: data.work_end_time.slice(0, 5),
            working_days: data.working_days.map(String),
            slot_duration: data.slot_duration,
          });
        }
      } catch (error) {
        console.error("Error:", error);
      }
    };

    fetchSettings();
  }, [user, form]);

  const onSubmit = async (data: ScheduleSettingsForm) => {
    if (!user) return;
    
    setLoading(true);
    try {
      const { error } = await supabase
        .from("schedule_settings")
        .upsert({
          trainer_id: user.id,
          work_start_time: data.work_start_time,
          work_end_time: data.work_end_time,
          working_days: data.working_days.map(Number),
          slot_duration: data.slot_duration,
        }, {
          onConflict: 'trainer_id'
        });

      if (error) throw error;
      toast.success(language === 'en' ? "Schedule settings updated successfully!" : "Configurações da agenda atualizadas com sucesso!");
      onSave?.();
    } catch (error) {
      toast.error(language === 'en' ? "Error updating schedule settings" : "Erro ao atualizar configurações da agenda");
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="dark:bg-gray-900">
      <CardHeader>
        <CardTitle>{t("settings.title")}</CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="work_start_time"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("settings.startTime")}</FormLabel>
                  <FormControl>
                    <Input type="time" {...field} className="dark:bg-gray-800 dark:text-white" />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="work_end_time"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("settings.endTime")}</FormLabel>
                  <FormControl>
                    <Input type="time" {...field} className="dark:bg-gray-800 dark:text-white" />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="working_days"
              render={() => (
                <FormItem>
                  <FormLabel>{t("settings.workingDays")}</FormLabel>
                  <div className="grid gap-2">
                    {weekDays.map((day) => (
                      <div key={day.value} className="flex items-center space-x-2">
                        <Checkbox
                          checked={form.watch("working_days")?.includes(day.value)}
                          onCheckedChange={(checked) => {
                            const current = form.getValues("working_days") || [];
                            const updated = checked
                              ? [...current, day.value]
                              : current.filter((value) => value !== day.value);
                            form.setValue("working_days", updated);
                          }}
                        />
                        <label
                          htmlFor={`day-${day.value}`}
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          {t(day.label)}
                        </label>
                      </div>
                    ))}
                  </div>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="slot_duration"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("settings.slotDuration")}</FormLabel>
                  <FormControl>
                    <Input 
                      type="number" 
                      {...field}
                      onChange={(e) => field.onChange(Number(e.target.value))} 
                      className="dark:bg-gray-800 dark:text-white"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <LoadingButton type="submit" loading={loading}>
              {t("settings.save")}
            </LoadingButton>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
